import { PaymentMethod } from '@lucidtech/las-sdk-browser';
import { memo, Suspense, useMemo, useState } from 'react';

import SecurePaymentByStripeLogo from '@/assets/secure-payment-by-stripe.svg?react';
import TriggerIcon from '@/assets/trigger-icon.svg?react';
import { merge } from '@/components';
import { Badge, Button, Flex, Heading, Label, Radio, RadioGroup, Save, Separator, Text } from '@/components/core';
import { CloseIcon } from '@/components/icons';
import { toPlanId, useGetPlan } from '@/hooks/api';
import { DivProps, isNumber } from '@/utils';

import { StripePayment } from './StripePayment';
import { useStripePayment } from './useStripePayment';

type BillingOptionProps = Omit<DivProps, 'children'> & {
  radioValue: string;
  title: string;
  price?: string;
  discount?: string;
};

const IBillingOption = ({ radioValue, title, price, discount, ...rest }: BillingOptionProps) => {
  return (
    <Flex
      className="gap-md cursor-pointer items-center rounded-lg border border-gray-200 bg-white p-3 hover:bg-gray-50"
      {...rest}
    >
      <Radio value={radioValue} />
      <Flex col>
        <Text className="font-semibold" slot="description">
          {title}
        </Text>
        {price ? (
          <Text size="xs" slot="description">
            {price}
          </Text>
        ) : (
          <div className="h-3 w-8 animate-pulse rounded-lg bg-gray-200" />
        )}
      </Flex>
      {discount ? (
        <>
          <div className="grow" />
          <Badge variant="success" label={discount} />
        </>
      ) : null}
    </Flex>
  );
};

const BillingOption = memo(IBillingOption) as typeof IBillingOption;

type UpgradePlanContentsProps = Omit<DivProps, 'children'> & {
  monthlyPlanId: string;
  yearlyPlanId: string;
  defaultPlanId: string;
  getPaymentMethodPromise?: Promise<PaymentMethod> | null;
  close?: () => void;
};

const IUpgradePlanContents = ({
  monthlyPlanId,
  yearlyPlanId,
  defaultPlanId,
  getPaymentMethodPromise,
  close,
  className,
  ...rest
}: UpgradePlanContentsProps) => {
  const { data: monthlyPlan } = useGetPlan(toPlanId(monthlyPlanId));
  const { data: yearlyPlan } = useGetPlan(toPlanId(yearlyPlanId));
  const [currentPlanId, setCurrentPlanId] = useState(defaultPlanId);
  const { data: currentPlan } = useGetPlan(toPlanId(currentPlanId));
  const { PaymentElement, PaymentButton } = useStripePayment({
    planId: currentPlanId,
    redirectUrl: '/_auth/_org/settings/plans',
    getPaymentMethodPromise,
    closeModal: close,
  });

  const autoRenewText = useMemo(() => {
    switch (currentPlanId) {
      case monthlyPlanId:
        return 'on the 1st of each month';
      case yearlyPlanId:
        return 'on the 1st every 12 months';
    }
  }, [currentPlanId, monthlyPlanId, yearlyPlanId]);

  const { monthlyPrice, yearlyPrice } = useMemo(() => {
    const monthlyFlatAmount = monthlyPlan?.license?.flatAmount;
    const yearlyFlatAmount = yearlyPlan?.license?.flatAmount;
    const yearlyBillingCycle = yearlyPlan?.license?.billingCycle;

    const monthlyPrice = isNumber(monthlyFlatAmount) ? `$${monthlyFlatAmount} / month` : undefined;
    const yearlyPrice =
      isNumber(yearlyFlatAmount) && isNumber(yearlyBillingCycle)
        ? `$${yearlyFlatAmount / yearlyBillingCycle} / month`
        : undefined;

    return {
      monthlyPrice: {
        title: 'Pay monthly',
        price: monthlyPrice,
      },
      yearlyPrice: {
        title: 'Pay annually',
        price: yearlyPrice,
      },
    };
  }, [monthlyPlan, yearlyPlan]);

  return (
    <Flex col className={merge('', className)} {...rest}>
      <Flex className="gap-sm items-center border-b border-gray-200 p-4">
        <TriggerIcon className="text-primary-500 size-5" />
        <Heading>Upgrade Plan to {currentPlan?.name}</Heading>

        <div className="grow" />
        {close ? (
          <Button variant="plain" onPress={close}>
            <CloseIcon />
          </Button>
        ) : null}
      </Flex>

      <Flex>
        <Flex className="w-1/2 p-4">
          <PaymentElement className="w-full" />
        </Flex>

        <Flex col className="gap-sm w-1/2 bg-gray-100 p-4">
          <Label>Billing options</Label>
          <RadioGroup value={currentPlanId} onChange={setCurrentPlanId}>
            <BillingOption
              onClick={() => setCurrentPlanId(monthlyPlanId)}
              radioValue={monthlyPlanId}
              {...monthlyPrice}
            />
            <BillingOption
              onClick={() => setCurrentPlanId(yearlyPlanId)}
              radioValue={yearlyPlanId}
              {...yearlyPrice}
              discount="Save 30%"
            />
          </RadioGroup>

          <Label>Summary</Label>
          <Flex col className="gap-sm rounded-lg border border-gray-200 bg-white p-4">
            <Flex>
              <Flex col>
                <Text className="font-semibold">{currentPlan?.name} plan</Text>
                <Text size="xs">Billed {currentPlan?.license?.billingCycle === 1 ? 'monthly' : 'yearly'}</Text>
              </Flex>
              <div className="grow" />
              {isNumber(currentPlan?.license?.flatAmount) ? (
                <Text className="font-semibold">${currentPlan?.license?.flatAmount}</Text>
              ) : (
                <div className="h-3 w-8 animate-pulse rounded-lg bg-gray-200" />
              )}
            </Flex>

            <Separator orientation="horizontal" />

            <Flex>
              <Text className="font-semibold">Total due today</Text>
              <div className="grow" />
              <Text className="font-semibold">$0</Text>
            </Flex>
          </Flex>

          <div className="grow" />

          <Text className="text-gray-500" size="xs">
            Your Cradl AI subscription will auto-renew {autoRenewText} at the above price unless cancelled.
          </Text>
          <PaymentButton />
          <SecurePaymentByStripeLogo className="w-full justify-center" />
        </Flex>
      </Flex>
    </Flex>
  );
};

const UpgradePlanContents = memo(IUpgradePlanContents) as typeof IUpgradePlanContents;

export type UpgradePlanProps = Omit<DivProps, 'children'> & UpgradePlanContentsProps;

const isLoadingPaymentMethod = <Save className="h-[8rem] w-[24rem]" message="Loading Payment Details" />;

const IUpgradePlan = ({ ...rest }: UpgradePlanProps) => {
  return (
    <StripePayment>
      <Suspense fallback={isLoadingPaymentMethod}>
        <UpgradePlanContents {...rest} />
      </Suspense>
    </StripePayment>
  );
};

export const UpgradePlan = memo(IUpgradePlan) as typeof IUpgradePlan;
